/**
 * UnifiedQueueDashboard - Unified queue organized by clinic (template)
 * 
 * Features:
 * - Real-time queue updates
 * - 1:1 template-to-clinic relationship (each template = a clinic queue)
 * - Support for both QR code and SMS check-ins
 * - Manual SMS check-in addition with clinic selection
 * - Patient notification via SMS
 * - Status management (waiting → called → completed)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  TablePagination,
} from '@mui/material';
import { contentCardSx } from '../../../../styles/standaloneLayoutTokens';
import {
  Add,
  Refresh,
  FilterList,
  QrCode2,
  Sms,
  People,
  LocalHospital,
  Business,
  DoNotDisturb,
  Close,
} from '@mui/icons-material';
import { useAuth } from '@healthdesk/shared-hooks';
import { tokens } from '@healthdesk/shared-utils';
import { db, getCheckInTemplates, getDepartments } from '@healthdesk/shared-services';
import {
  getTodayVisits,
  updateVisitStatus,
  sendPatientNotification,
} from '@healthdesk/shared-services';
import UnifiedQueuePatient from './UnifiedQueuePatient';
import CheckInOverrideDialog from './CheckInOverrideDialog';

const QUEUE_LIST_DEFAULT_ROWS_PER_PAGE = 15;
const QUEUE_LIST_PAGE_SIZE_OPTIONS = [10, 15, 25, 50];
const SUPERVISOR_CONV_TEMPLATE_CALLOUT_KEY = 'hd_supervisor_conv_template_callout_dismissed';

/**
 * Tab panel component
 */
const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

/**
 * Main UnifiedQueueDashboard component
 */
const UnifiedQueueDashboard = () => {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [visits, setVisits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedClinicId, setSelectedClinicId] = useState('all'); // 'all' or templateId
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('all'); // 'all' or departmentId (for facility admins)
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newPatientPhone, setNewPatientPhone] = useState('');
  const [selectedTemplateForAdd, setSelectedTemplateForAdd] = useState('');
  const [addingPatient, setAddingPatient] = useState(false);
  const [notifyingId, setNotifyingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectingId, setSelectingId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineVisit, setDeclineVisit] = useState(null);
  const [declineReason, setDeclineReason] = useState('insurance_not_accepted');
  const [declineCustomMessage, setDeclineCustomMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [queueListPage, setQueueListPage] = useState(0);
  const [queueListRowsPerPage, setQueueListRowsPerPage] = useState(QUEUE_LIST_DEFAULT_ROWS_PER_PAGE);
  const [showConversationTemplateCallout, setShowConversationTemplateCallout] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SUPERVISOR_CONV_TEMPLATE_CALLOUT_KEY) !== '1';
  });

  // Get facility ID (parent container for all clinics/templates)
  const facilityId = userData?.facilityId || userData?.clinicId || userData?.location;
  
  // Check user role for filtering
  const userRole = userData?.role;
  const isFacilityAdmin = userRole === 'facility' || userRole === 'chartmind-admin' || userRole === 'chartmind-kijabe';
  const isDepartmentAdmin = userRole === 'department-admin';
  const isClinicSupervisor = userRole === 'chartmind-supervisor';
  const isProfessional = userRole === 'professional-chartmind';
  const userDepartmentId = userData?.departmentId;
  const userClinicId = userData?.clinicId;
  
  // For professionals with multi-clinic access, get their assigned clinic IDs
  const userAssignedClinicIds = React.useMemo(() => {
    if (!isProfessional) return [];
    
    // Get clinic IDs from facilities array (supports multi-clinic professionals)
    const facilities = userData?.facilities || [];
    const clinicIds = facilities
      .filter(f => f.facilityId === facilityId && f.clinicId)
      .map(f => f.clinicId);
    
    // Fallback to single clinicId if facilities array is empty
    if (clinicIds.length === 0 && userClinicId) {
      return [userClinicId];
    }
    
    return clinicIds;
  }, [isProfessional, userData?.facilities, facilityId, userClinicId]);

  // Load templates (clinics) and visits
  useEffect(() => {
    if (facilityId) {
      loadTemplates();
      loadVisits();
      // Set up real-time listener
      const unsubscribe = subscribeToVisits();
      return () => unsubscribe?.();
    } else {
      setError('Facility ID not found. Please ensure your account is associated with a facility.');
      setLoading(false);
      setTemplatesLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    setQueueListPage(0);
  }, [selectedClinicId, statusFilter, selectedDepartmentId]);

  const loadTemplates = async () => {
    if (!facilityId) return;
    
    setTemplatesLoading(true);
    try {
      // Load templates and departments in parallel
      const [loadedTemplates, loadedDepartments] = await Promise.all([
        getCheckInTemplates(facilityId),
        getDepartments(facilityId),
      ]);
      
      setTemplates(loadedTemplates);
      setDepartments(loadedDepartments);
      
      // Filter templates based on user role
      let availableTemplates = loadedTemplates;
      
      // For professionals, filter to only their assigned clinics
      if (isProfessional && userAssignedClinicIds.length > 0) {
        availableTemplates = loadedTemplates.filter(t => userAssignedClinicIds.includes(t.id));
        
        // Auto-select if professional has only ONE clinic
        if (availableTemplates.length === 1) {
          setSelectedClinicId(availableTemplates[0].id);
        }
      }
      
      // For clinic supervisors, auto-select their clinic
      if (isClinicSupervisor && userClinicId) {
        availableTemplates = loadedTemplates.filter(t => t.id === userClinicId);
        if (availableTemplates.length === 1) {
          setSelectedClinicId(availableTemplates[0].id);
        }
      }
      
      // For department admins, filter to only their department
      if (isDepartmentAdmin && userDepartmentId) {
        availableTemplates = loadedTemplates.filter(t => t.departmentId === userDepartmentId);
      }
      
      // Set default template for adding patients (use the default template or first one)
      const defaultTemplate = availableTemplates.find(t => t.isDefault) || availableTemplates[0];
      if (defaultTemplate) {
        setSelectedTemplateForAdd(defaultTemplate.id);
      }
    } catch (err) {
      console.error('[UnifiedQueue] Error loading templates:', err);
      // Don't set error - templates are optional, can still show visits
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadVisits = async () => {
    if (!facilityId) return;
    
    setLoading(true);
    setError(null);
    try {
      const todayVisits = await getTodayVisits(facilityId);
      setVisits(todayVisits);
    } catch (err) {
      console.error('[UnifiedQueue] Error loading visits:', err);
      setError('Failed to load queue');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToVisits = () => {
    if (!facilityId) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return db
      .collection('clinics')
      .doc(facilityId)
      .collection('visits')
      .where('checkedInAt', '>=', today)
      .orderBy('checkedInAt', 'desc')
      .onSnapshot(
        (snapshot) => {
          const updatedVisits = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            checkedInAt: doc.data().checkedInAt?.toDate?.() || doc.data().checkedInAt,
            calledAt: doc.data().calledAt?.toDate?.() || doc.data().calledAt,
          }));
          setVisits(updatedVisits);
          setLoading(false);
        },
        (err) => {
          console.error('[UnifiedQueue] Subscription error:', err);
          setError('Failed to subscribe to queue updates');
        }
      );
  };

  // Get template name by ID
  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template?.name || 'Unknown Clinic';
  };

  // Get department name by ID
  const getDepartmentName = (departmentId) => {
    const dept = departments.find(d => d.id === departmentId);
    return dept?.name || 'Unknown Department';
  };

  // Get templates filtered by user's access
  const getAccessibleTemplates = () => {
    // Professionals: filter to only their assigned clinics if they have assignments
    // If no specific clinic assignments, show ALL clinics in their facility (read-only queue access)
    if (isProfessional) {
      if (userAssignedClinicIds.length > 0) {
        return templates.filter(t => userAssignedClinicIds.includes(t.id));
      }
      // Professional with facility but no specific clinic - show all clinics
      return templates;
    }
    // Clinic supervisors: filter to only their clinic
    if (isClinicSupervisor && userClinicId) {
      return templates.filter(t => t.id === userClinicId);
    }
    // Department admins: filter to their department
    if (isDepartmentAdmin && userDepartmentId) {
      return templates.filter(t => t.departmentId === userDepartmentId);
    }
    // Facility admins: filter by selected department if not 'all'
    if (isFacilityAdmin && selectedDepartmentId !== 'all') {
      return templates.filter(t => t.departmentId === selectedDepartmentId);
    }
    return templates;
  };

  // Filter visits based on selected department, clinic and status filter
  const getFilteredVisits = () => {
    let filtered = visits;

    // For professionals: 
    // - If they have assigned clinics AND there are matching templates, filter to those
    // - Otherwise, show all visits in the facility (read-only queue access)
    if (isProfessional && userAssignedClinicIds.length > 0) {
      // Only filter if at least one of their assigned clinics exists as a template
      const hasMatchingTemplates = templates.some(t => userAssignedClinicIds.includes(t.id));
      if (hasMatchingTemplates) {
        filtered = filtered.filter(v => userAssignedClinicIds.includes(v.templateId));
      }
      // If no matching templates, show all visits (they have facility access but no specific clinic assignment)
    }
    
    // For clinic supervisors, filter to only their clinic (if it exists as a template)
    if (isClinicSupervisor && userClinicId) {
      // Only filter if the supervisor's clinic exists as a template
      const clinicTemplateExists = templates.some(t => t.id === userClinicId);
      if (clinicTemplateExists) {
        filtered = filtered.filter(v => v.templateId === userClinicId);
      }
      // If clinic template doesn't exist, show all visits in facility
    }

    // For department admins, filter to only their department's clinics
    if (isDepartmentAdmin && userDepartmentId) {
      const deptTemplateIds = templates
        .filter(t => t.departmentId === userDepartmentId)
        .map(t => t.id);
      // Only filter if there are department templates
      if (deptTemplateIds.length > 0) {
        filtered = filtered.filter(v => deptTemplateIds.includes(v.templateId));
      }
    }
    
    // For facility admins with department filter selected
    if (isFacilityAdmin && selectedDepartmentId !== 'all') {
      const deptTemplateIds = templates
        .filter(t => t.departmentId === selectedDepartmentId)
        .map(t => t.id);
      if (deptTemplateIds.length > 0) {
        filtered = filtered.filter(v => deptTemplateIds.includes(v.templateId));
      }
    }

    // Filter by clinic (templateId) - explicit selection
    if (selectedClinicId !== 'all') {
      filtered = filtered.filter(v => v.templateId === selectedClinicId);
    }

    // Filter by status
    if (statusFilter === 'active') {
      filtered = filtered.filter(v => v.status !== 'completed' && v.status !== 'declined');
    } else if (statusFilter === 'waiting') {
      filtered = filtered.filter(v => v.status === 'checked_in');
    } else if (statusFilter === 'called') {
      filtered = filtered.filter(v => v.status === 'called' || v.status === 'in_progress');
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(v => v.status === 'completed');
    } else if (statusFilter === 'declined') {
      filtered = filtered.filter(v => v.status === 'declined');
    }

    // Sort by check-in time (oldest first for waiting, newest first for completed)
    return filtered.sort((a, b) => {
      const timeA = new Date(a.checkedInAt).getTime();
      const timeB = new Date(b.checkedInAt).getTime();
      return statusFilter === 'completed' ? timeB - timeA : timeA - timeB;
    });
  };

  // Get accessible visits (filtered by role-based access)
  const getAccessibleVisits = () => {
    let filtered = visits;
    
    // Professionals: only filter if they have assigned clinics that match existing templates
    if (isProfessional && userAssignedClinicIds.length > 0) {
      const hasMatchingTemplates = templates.some(t => userAssignedClinicIds.includes(t.id));
      if (hasMatchingTemplates) {
        filtered = filtered.filter(v => userAssignedClinicIds.includes(v.templateId));
      }
      // If no matching templates, show all visits (read-only facility queue access)
    }
    
    // Clinic supervisors: only their clinic (if it exists as a template)
    if (isClinicSupervisor && userClinicId) {
      const clinicTemplateExists = templates.some(t => t.id === userClinicId);
      if (clinicTemplateExists) {
        filtered = filtered.filter(v => v.templateId === userClinicId);
      }
      // If clinic template doesn't exist, show all visits in facility
    }
    
    // Department admins: only their department's clinics (if templates exist)
    if (isDepartmentAdmin && userDepartmentId) {
      const deptTemplateIds = templates
        .filter(t => t.departmentId === userDepartmentId)
        .map(t => t.id);
      if (deptTemplateIds.length > 0) {
        filtered = filtered.filter(v => deptTemplateIds.includes(v.templateId));
      }
    }
    
    // Facility admins: filter by selected department (if templates exist)
    if (isFacilityAdmin && selectedDepartmentId !== 'all') {
      const deptTemplateIds = templates
        .filter(t => t.departmentId === selectedDepartmentId)
        .map(t => t.id);
      if (deptTemplateIds.length > 0) {
        filtered = filtered.filter(v => deptTemplateIds.includes(v.templateId));
      }
    }
    
    return filtered;
  };

  // Count active visits (respecting department filter)
  const accessibleVisits = getAccessibleVisits();
  const totalActive = accessibleVisits.filter(v => v.status !== 'completed').length;
  
  // Count by clinic
  const getClinicCount = (templateId) => {
    return accessibleVisits.filter(v => v.templateId === templateId && v.status !== 'completed').length;
  };
  
  // Count by department
  const getDepartmentCount = (departmentId) => {
    const deptTemplateIds = templates.filter(t => t.departmentId === departmentId).map(t => t.id);
    return visits.filter(v => deptTemplateIds.includes(v.templateId) && v.status !== 'completed').length;
  };
  
  // Count by check-in method (for stats)
  const qrCount = visits.filter(v => v.checkInMethod === 'qr_code' && v.status !== 'completed').length;
  const smsCount = visits.filter(v => v.checkInMethod === 'sms' && v.status !== 'completed').length;

  // Handle notify patient
  const handleNotify = async (visit) => {
    const phone = visit.phone || visit.cmrSnapshot?.['patient.phone'];
    if (!phone) {
      setError('No phone number available for this patient');
      return;
    }

    setNotifyingId(visit.id);
    try {
      // Update status to called
      await updateVisitStatus(facilityId, visit.id, 'called');
      
      // Send SMS notification (using messages collection for Firebase function to send)
      // Use the clinic name from the template if available
      const clinicTemplateName = getTemplateName(visit.templateId);
      const facilityName = userData?.facilityName || userData?.name || 'The clinic';
      const notificationName = clinicTemplateName !== 'Unknown Clinic' ? clinicTemplateName : facilityName;
      
      await db.collection('messages').add({
        to: phone,
        body: `${notificationName} is ready for you. Please proceed to the check-in desk.`,
        createdAt: new Date(),
        visitId: visit.id,
        facilityId: facilityId,
        templateId: visit.templateId,
      });

      setSuccess('Patient notified successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[UnifiedQueue] Notify error:', err);
      setError('Failed to notify patient: ' + err.message);
    } finally {
      setNotifyingId(null);
    }
  };

  // Handle status update
  const handleUpdateStatus = async (visit, newStatus) => {
    setUpdatingId(visit.id);
    try {
      await updateVisitStatus(facilityId, visit.id, newStatus);
      setSuccess(`Patient marked as ${newStatus}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[UnifiedQueue] Status update error:', err);
      setError('Failed to update status: ' + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  // Handle delete visit
  const handleDelete = async (visit) => {
    if (!window.confirm('Remove this patient from the queue?')) return;

    setDeletingId(visit.id);
    try {
      await db
        .collection('clinics')
        .doc(facilityId)
        .collection('visits')
        .doc(visit.id)
        .delete();
      
      setSuccess('Patient removed from queue');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[UnifiedQueue] Delete error:', err);
      setError('Failed to remove patient: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Predefined decline reasons with human-readable messages
  const DECLINE_REASONS = {
    insurance_not_accepted: 'We are unable to accept your insurance at this time. Please contact your insurance provider for a list of in-network facilities.',
    no_availability: 'We are unable to accommodate your visit at this time due to capacity. We apologize for the inconvenience.',
    referral_required: 'A referral from your primary care provider is required before we can see you. Please obtain a referral and reschedule.',
    custom: '', // user types their own message
  };

  // Open decline dialog
  const handleOpenDecline = (visit) => {
    setDeclineVisit(visit);
    setDeclineReason('insurance_not_accepted');
    setDeclineCustomMessage('');
    setDeclineDialogOpen(true);
  };

  // Execute decline
  const handleConfirmDecline = async () => {
    if (!declineVisit) return;

    const reasonKey = declineReason;
    const message = reasonKey === 'custom' 
      ? declineCustomMessage 
      : DECLINE_REASONS[reasonKey];

    if (!message.trim()) {
      setError('Please provide a decline message.');
      return;
    }

    setDecliningId(declineVisit.id);
    setDeclineDialogOpen(false);

    try {
      // Update visit status to declined with reason and message
      await updateVisitStatus(facilityId, declineVisit.id, 'declined', {
        declineReason: reasonKey,
        statusMessage: message,
      });

      // Send SMS notification to the patient with the decline message
      const clinicTemplateName = getTemplateName(declineVisit.templateId);
      const facilityName = userData?.facilityName || userData?.name || 'The clinic';
      const senderName = clinicTemplateName !== 'Unknown Clinic' ? clinicTemplateName : facilityName;

      try {
        await sendPatientNotification(
          facilityId, 
          declineVisit.id, 
          `${senderName}: ${message}`
        );
      } catch (smsErr) {
        console.warn('[UnifiedQueue] SMS notification failed (patient still notified in-app):', smsErr);
      }

      setSuccess('Patient has been declined and notified');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('[UnifiedQueue] Decline error:', err);
      setError('Failed to decline visit: ' + err.message);
    } finally {
      setDecliningId(null);
      setDeclineVisit(null);
    }
  };

  // Handle select patient - navigate to ChartMind recording with patient context
  const handleSelectPatient = async (visit) => {
    setSelectingId(visit.id);
    try {
      // Update visit status to in_progress
      await updateVisitStatus(facilityId, visit.id, 'in_progress');
      
      // Navigate to ChartMind recording page with visit context
      // Store visit data in sessionStorage for ChartMind to pick up
      // `checkInMethod` (e.g. targeted_invite) is read in ChartMind via `chartmind_visit_context`
      // → `usePatientLinking` / `buildPatientFromSnapshot` for headers and CMR merge behavior.
      const visitContext = {
        visitId: visit.id,
        facilityId: facilityId,
        patientId: visit.patientId,
        patientName: visit.cmrSnapshot?.['patient.fullName'] || 
                     `${visit.cmrSnapshot?.['patient.firstName'] || ''} ${visit.cmrSnapshot?.['patient.lastName'] || ''}`.trim() ||
                     'Unknown Patient',
        patientDOB: visit.cmrSnapshot?.['patient.dateOfBirth'],
        patientPhone: visit.cmrSnapshot?.['patient.phone'] || visit.phone,
        cmrSnapshot: visit.cmrSnapshot,
        customFieldResponses: visit.customFieldResponses,
        checkInMethod: visit.checkInMethod,
        templateId: visit.templateId,
        checkedInAt: visit.checkedInAt,
        conversationResults: visit.conversationResults || null,
        rosResults: visit.rosResults || null,
        idScan: visit.idScan || null,
        insuranceScan: visit.insuranceScan || null,
      };
      sessionStorage.setItem('chartmind_visit_context', JSON.stringify(visitContext));
      
      // Navigate to ChartMind recording page
      navigate('/dashboard/chartmind/recording', { 
        state: { visitContext } 
      });
    } catch (err) {
      console.error('[UnifiedQueue] Select patient error:', err);
      setError('Failed to select patient: ' + err.message);
    } finally {
      setSelectingId(null);
    }
  };

  // Handle resume session - navigate to ChartMind with existing draft
  const handleResumeSession = (visit) => {
    if (visit.chartmindDraftId) {
      navigate(`/dashboard/chartmind/recording?draft=${visit.chartmindDraftId}`);
    } else {
      console.warn('[UnifiedQueue] Cannot resume session - no draft linked to visit:', visit.id);
    }
  };

  // Handle manual SMS check-in
  const handleAddSMSCheckIn = async () => {
    if (!newPatientPhone.trim()) {
      setError('Please enter a phone number');
      return;
    }
    
    if (!selectedTemplateForAdd) {
      setError('Please select a clinic');
      return;
    }

    setAddingPatient(true);
    try {
      // Get next patient number for this clinic
      const clinicVisits = visits.filter(v => v.templateId === selectedTemplateForAdd && v.checkInMethod === 'sms');
      const nextNumber = clinicVisits.length + 1;

      // Create SMS check-in visit
      await db
        .collection('clinics')
        .doc(facilityId)
        .collection('visits')
        .add({
          clinicId: facilityId,
          templateId: selectedTemplateForAdd,
          phone: newPatientPhone.trim(),
          patientNumber: nextNumber,
          checkInMethod: 'sms',
          status: 'checked_in',
          checkedInAt: new Date(),
          createdBy: user?.uid,
        });

      const clinicName = getTemplateName(selectedTemplateForAdd);
      setSuccess(`Patient #${nextNumber} added to ${clinicName} queue`);
      setNewPatientPhone('');
      setAddDialogOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[UnifiedQueue] Add SMS check-in error:', err);
      setError('Failed to add patient: ' + err.message);
    } finally {
      setAddingPatient(false);
    }
  };

  if (!facilityId) {
    return (
      <Alert severity="warning">
        Facility ID not found. Please ensure your account is associated with a facility.
      </Alert>
    );
  }

  const filteredVisits = getFilteredVisits();
  const queueMaxPage = Math.max(0, Math.ceil(filteredVisits.length / queueListRowsPerPage) - 1);
  const queuePageSafe = Math.min(queueListPage, queueMaxPage);
  const paginatedVisits = filteredVisits.slice(
    queuePageSafe * queueListRowsPerPage,
    queuePageSafe * queueListRowsPerPage + queueListRowsPerPage
  );

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ mt: { xs: 1, sm: 5 } }}>
            Clinic Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalActive} patient{totalActive !== 1 ? 's' : ''} waiting today
            {templates.length > 1 && ` across ${templates.length} clinics`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, sm: 5 } }}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => { loadTemplates(); loadVisits(); }} disabled={loading || templatesLoading}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={() => setOverrideDialogOpen(true)}
            disabled={templates.length === 0}
          >
            Check in with override
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
            disabled={templates.length === 0}
          >
            Add Patient
          </Button>
        </Box>
      </Box>

      <CheckInOverrideDialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        onSuccess={() => {
          setSuccess('Patient checked in with staff override');
          setTimeout(() => setSuccess(null), 3000);
          loadVisits();
        }}
        facilityId={facilityId}
        templates={getAccessibleTemplates()}
        staffUid={user?.uid}
      />

      {isClinicSupervisor && showConversationTemplateCallout && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                onClick={() => navigate('/dashboard/supervision/custom-fields')}
              >
                Open Check-In Settings →
              </Button>
              <IconButton
                size="small"
                onClick={() => {
                  localStorage.setItem(SUPERVISOR_CONV_TEMPLATE_CALLOUT_KEY, '1');
                  setShowConversationTemplateCallout(false);
                }}
                aria-label="Dismiss conversation templates callout"
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
        >
          New: Assign targeted AI conversations to your check-in template in Check-In Settings.
        </Alert>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats */}
      <Paper elevation={0} sx={{ ...contentCardSx, p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <People color="primary" />
            <Typography variant="h6">{totalActive}</Typography>
            <Typography variant="body2" color="text.secondary">Active</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QrCode2 color="primary" />
            <Typography variant="h6">{qrCount}</Typography>
            <Typography variant="body2" color="text.secondary">QR Check-ins</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Sms color="secondary" />
            <Typography variant="h6">{smsCount}</Typography>
            <Typography variant="body2" color="text.secondary">SMS Check-ins</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          
          {/* Department filter - only for facility admins */}
          {isFacilityAdmin && departments.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartmentId}
                label="Department"
                onChange={(e) => {
                  setSelectedDepartmentId(e.target.value);
                  setSelectedClinicId('all'); // Reset clinic filter when department changes
                }}
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business fontSize="small" />
                    All Departments
                  </Box>
                </MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept.id} value={dept.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business fontSize="small" />
                        {dept.name}
                      </Box>
                      <Chip label={getDepartmentCount(dept.id)} size="small" sx={{ ml: 1, height: 20 }} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="waiting">Waiting</MenuItem>
              <MenuItem value="called">Called</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="declined">Declined</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Clinic Tabs */}
      <Paper elevation={0} sx={{ ...contentCardSx, mb: 3 }}>
        {templatesLoading ? (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading clinics...</Typography>
          </Box>
        ) : getAccessibleTemplates().length === 0 && visits.length === 0 ? (
          // Only show "no templates" message if there are ALSO no visits
          <Alert severity="info" sx={{ m: 2 }}>
            {isDepartmentAdmin 
              ? 'No check-in forms configured for your department yet.'
              : selectedDepartmentId !== 'all'
                ? `No check-in forms configured in ${getDepartmentName(selectedDepartmentId)} yet.`
                : 'No check-in forms configured yet. Create one in Clinic Manager to start accepting patients.'
            }
          </Alert>
        ) : (
          <>
            <Tabs 
              value={selectedClinicId}
              onChange={(e, v) => setSelectedClinicId(v)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                value="all" 
                label={getAccessibleTemplates().length > 0 ? `All Clinics (${totalActive})` : `Queue (${totalActive})`}
                icon={<Business sx={{ fontSize: '1rem' }} />}
                iconPosition="start"
              />
              {getAccessibleTemplates().map(template => (
                <Tab 
                  key={template.id}
                  value={template.id}
                  label={`${template.name} (${getClinicCount(template.id)})`}
                  icon={<LocalHospital sx={{ fontSize: '1rem' }} />}
                  iconPosition="start"
                />
              ))}
            </Tabs>

            <Box sx={{ p: 2 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : filteredVisits.length === 0 ? (
                <Alert severity="info">
                  {selectedClinicId === 'all' 
                    ? 'No patients in queue matching your filter.'
                    : `No patients in ${getTemplateName(selectedClinicId)} queue matching your filter.`
                  }
                </Alert>
              ) : (
                <>
                  {paginatedVisits.map(visit => (
                    <UnifiedQueuePatient
                      key={visit.id}
                      visit={visit}
                      clinicName={getTemplateName(visit.templateId)}
                      showClinicName={selectedClinicId === 'all'}
                      onNotify={handleNotify}
                      onUpdateStatus={handleUpdateStatus}
                      onDelete={handleDelete}
                      onDecline={handleOpenDecline}
                      onSelectPatient={handleSelectPatient}
                      onResumeSession={isProfessional ? handleResumeSession : undefined}
                      notifyLoading={notifyingId === visit.id}
                      updateLoading={updatingId === visit.id}
                      deleteLoading={deletingId === visit.id}
                      declineLoading={decliningId === visit.id}
                      selectLoading={selectingId === visit.id}
                      canSelectPatient={isProfessional}
                    />
                  ))}
                  <TablePagination
                    component="div"
                    count={filteredVisits.length}
                    page={queuePageSafe}
                    onPageChange={(_, nextPage) =>
                      setQueueListPage(Math.min(nextPage, queueMaxPage))
                    }
                    rowsPerPage={queueListRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setQueueListRowsPerPage(Number.parseInt(e.target.value, 10));
                      setQueueListPage(0);
                    }}
                    rowsPerPageOptions={QUEUE_LIST_PAGE_SIZE_OPTIONS}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                    sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}
                  />
                </>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* Add Patient Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => !addingPatient && setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add color="primary" />
            Add Patient to Queue
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manually add a patient who called or walked in to check in.
          </Typography>
          
          {/* Clinic Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Clinic</InputLabel>
            <Select
              value={selectedTemplateForAdd}
              label="Select Clinic"
              onChange={(e) => setSelectedTemplateForAdd(e.target.value)}
            >
              {getAccessibleTemplates().map(template => (
                <MenuItem key={template.id} value={template.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalHospital fontSize="small" color="primary" />
                    {template.name}
                    {template.isDefault && (
                      <Chip label="Default" size="small" sx={{ ml: 1, height: 20 }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Phone Number */}
          <TextField
            label="Phone Number"
            type="tel"
            fullWidth
            value={newPatientPhone}
            onChange={(e) => setNewPatientPhone(e.target.value)}
            placeholder="+1 (555) 123-4567"
            helperText="Enter the patient's phone number for notifications"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addingPatient}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddSMSCheckIn} 
            variant="contained"
            disabled={addingPatient || !newPatientPhone.trim() || !selectedTemplateForAdd}
          >
            {addingPatient ? 'Adding...' : 'Add to Queue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline Visit Dialog */}
      <Dialog
        open={declineDialogOpen}
        onClose={() => setDeclineDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DoNotDisturb sx={{ color: tokens.colors.msiRed.light }} />
            <Typography variant="h6">Decline Patient Visit</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a reason for declining this visit. The patient will be notified both on-screen and via SMS.
          </Typography>

          <RadioGroup
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          >
            <FormControlLabel
              value="insurance_not_accepted"
              control={<Radio sx={{ '&.Mui-checked': { color: tokens.colors.msiRed.light } }} />}
              label="Insurance not accepted"
            />
            <FormControlLabel
              value="no_availability"
              control={<Radio sx={{ '&.Mui-checked': { color: tokens.colors.msiRed.light } }} />}
              label="No availability / At capacity"
            />
            <FormControlLabel
              value="referral_required"
              control={<Radio sx={{ '&.Mui-checked': { color: tokens.colors.msiRed.light } }} />}
              label="Referral required"
            />
            <FormControlLabel
              value="custom"
              control={<Radio sx={{ '&.Mui-checked': { color: tokens.colors.msiRed.light } }} />}
              label="Custom message"
            />
          </RadioGroup>

          {declineReason === 'custom' ? (
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              label="Custom decline message"
              placeholder="Enter a message that will be shown to the patient..."
              value={declineCustomMessage}
              onChange={(e) => setDeclineCustomMessage(e.target.value)}
              sx={{ mt: 2 }}
            />
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Message to patient:</strong> {DECLINE_REASONS[declineReason]}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDecline}
            disabled={declineReason === 'custom' && !declineCustomMessage.trim()}
            sx={{ 
              bgcolor: tokens.colors.msiRed.light, 
              '&:hover': { bgcolor: tokens.colors.msiRedHoverBorder.light },
            }}
            startIcon={<DoNotDisturb />}
          >
            Decline & Notify Patient
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnifiedQueueDashboard;
